import React, { useCallback, useMemo, useRef, useState } from "react";

import { localPoint } from "@visx/event";
import { scaleLinear } from "@visx/scale";
import { find, isEmpty } from "lodash/fp";

import { Stack, SxProps } from "@mui/material";

import * as Events from "@app/analytics/tracking/events";
import { createNormalize } from "@app/analytics/utils";
import { isWithinRange } from "@app/common/utils";

import Scrubber from "./Scrubber";
import ScrubberBarTooltip from "./ScrubberBarTooltip";
import {
  Block,
  BlocksWrapper,
  Marker,
  MarkersWrapper,
  Segment,
  SegmentsWrapper,
} from "./styles";
import {
  getIntersectingAnnotationsFromTimeFrame,
  getIntersectingAnnotationsFromTimestamp,
} from "./utils";
import {
  Block as BlockType,
  Marker as MarkerType,
  Segment as SegmentType,
} from "../PlayerControls/types";

const MARKER_WIDTH = 8;

export interface ScrubberBarProps {
  /** current time in seconds **/
  currentTime: number;
  /** duration in seconds **/
  duration: number;
  blocks?: BlockType[];
  markers?: MarkerType[];
  onChange?: (seconds: number) => void;
  onClickBlockOrMarker?: (seconds: number) => void;
  segments?: SegmentType[];
  sx?: SxProps;
  /** whether the scrubber includes/should show blocks, markers, and segments */
  withAnnotations?: boolean;
}

type ScrubberTooltipContent = Pick<ScrubberBarProps, "blocks" | "markers"> & {
  invert?: boolean;
  x?: number;
};

const ScrubberBar = ({
  currentTime,
  duration,
  blocks = [],
  markers = [],
  segments = [],
  onChange,
  onClickBlockOrMarker,
  withAnnotations = false,
}: ScrubberBarProps) => {
  const [tooltipContent, setTooltipContent] = useState<ScrubberTooltipContent>(
    {}
  );
  const ref = useRef<HTMLDivElement>(null);
  const normalize = createNormalize(0, duration);

  const width = ref.current?.offsetWidth ?? 0;

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, duration],
        range: [0, width],
      }),
    [duration, width]
  );

  const markerOffset = useMemo(() => xScale(MARKER_WIDTH), [xScale]);
  const invertThreshold = useMemo(() => xScale(duration * 0.6), [xScale]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      const point = localPoint(event);
      const x = point?.x ?? 0;
      const timeInSeconds = xScale.invert(x);
      setTooltipContent({
        invert: x > invertThreshold,
        x,
        ...getIntersectingAnnotationsFromTimestamp(
          timeInSeconds,
          blocks,
          markers,
          segments,
          markerOffset
        ),
      });
    },
    [xScale, blocks.length, markers.length]
  );

  const handleMarkerHover = useCallback(
    (event: React.MouseEvent) => {
      const targetMarker = find({ id: event.currentTarget.id }, markers)!;
      const scrubberLeft = ref.current?.getBoundingClientRect().left ?? 0;
      const markerLeft =
        event.currentTarget.getBoundingClientRect().left + MARKER_WIDTH / 2;
      const x = Math.max(markerLeft - scrubberLeft, 0);
      const markerDuration = xScale(MARKER_WIDTH);

      const timeFrame = {
        start: targetMarker.startTime,
        end: Math.min(
          targetMarker.startTime + markerDuration,
          targetMarker.endTime
        ),
      };

      setTooltipContent({
        x,
        invert: x > invertThreshold,
        ...getIntersectingAnnotationsFromTimeFrame(
          timeFrame,
          blocks,
          markers,
          segments
        ),
      });
    },
    [xScale, blocks.length, markers.length]
  );

  const clearTooltip = useCallback(() => setTooltipContent({}), []);

  // placement of the annotations is based on a % of the duration; if no duration,
  // don't show them, since we won't be able to accurately place them anyway
  const shouldShowAnnotations = withAnnotations && Boolean(duration);

  const markersToShow = shouldShowAnnotations ? markers : [];
  const blocksToShow = shouldShowAnnotations ? blocks : [];
  const segmentsToShow = shouldShowAnnotations ? segments : [];

  const handleBlockOrMarkerClick = (blockOrMarker: MarkerType | BlockType) => {
    if (onClickBlockOrMarker) {
      return () => {
        Events.clickBlockOrMarker({
          id: blockOrMarker.id,
          label: blockOrMarker.label,
        });
        return onClickBlockOrMarker(blockOrMarker.startTime);
      };
    }
  };

  return (
    <Stack
      sx={{ width: "100%", py: 0.5, position: "relative" }}
      spacing="2px"
      className="scrubber"
    >
      <MarkersWrapper className="scrubber__markers">
        {markersToShow.map((marker) => {
          const isHighlighted =
            isWithinRange(currentTime, [marker.startTime, marker.endTime], {
              inclusiveEnd: true,
            }) || tooltipContent.markers?.includes(marker);

          return (
            <Marker
              id={marker.id}
              key={marker.id}
              color={marker.color}
              onMouseEnter={handleMarkerHover}
              onMouseLeave={clearTooltip}
              onClick={handleBlockOrMarkerClick(marker)}
              bgcolor={marker.bgcolor}
              className={isHighlighted ? "highlighted" : ""}
              sx={{ left: `${normalize(marker.startTime)}%` }}
            />
          );
        })}
      </MarkersWrapper>
      <Stack sx={{ width: "100%", height: "6px", position: "relative" }}>
        <SegmentsWrapper>
          {segmentsToShow.map((segment, i) => {
            const isLastSegment = i === segmentsToShow.length - 1;
            const rawWidth = normalize(segment.endTime - segment.startTime);
            const maxWidth = normalize(duration - segment.startTime);
            // force the last segment to extend to the end of the duration
            const width = isLastSegment ? maxWidth : rawWidth;
            return (
              <Segment
                id={segment.id}
                key={segment.id}
                sx={{
                  left: `${normalize(segment.startTime)}%`,
                  // for all but the last segment, give some space between this and the next segment
                  width: `calc(${width}% - ${isLastSegment ? "0px" : "3px"})`,
                }}
              />
            );
          })}
        </SegmentsWrapper>
        <BlocksWrapper>
          {blocksToShow.map((block) => {
            const isHighlighted =
              isWithinRange(currentTime, [block.startTime, block.endTime], {
                inclusiveEnd: true,
              }) || tooltipContent.blocks?.includes(block);

            return (
              <Block
                key={block.id}
                color={block.color}
                className={isHighlighted ? "highlighted" : ""}
                onClick={handleBlockOrMarkerClick(block)}
                sx={{
                  left: `${normalize(block.startTime)}%`,
                  width: `${normalize(block.endTime - block.startTime)}%`,
                }}
              />
            );
          })}
        </BlocksWrapper>
        <Stack
          onMouseMove={withAnnotations ? handleMouseMove : undefined}
          onMouseLeave={clearTooltip}
          ref={ref}
        >
          <Scrubber
            max={duration}
            value={currentTime}
            onChange={onChange}
            segmented={!isEmpty(segmentsToShow)}
          />
        </Stack>
      </Stack>
      <ScrubberBarTooltip {...tooltipContent} />
    </Stack>
  );
};

export default ScrubberBar;
